"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ShieldAlert, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqItems = [{
  id: "item-1",
  question: "Which building materials and personal belongings can typically be salvaged after a fire?",
  answer: "Dense structural framing, masonry, tile, and solid hardwoods can often be cleaned, sanitized, and sealed. Porous items such as exposed drywall, blown insulation, open mattresses, and unsealed food items must be discarded due to toxic soot penetration. For personal belongings, our certified technicians separate salvageable hard goods, electronics, and textiles for specialized ultrasonic cleaning and ozone chamber treatment before writing off non-recoverable items."
}, {
  id: "item-2",
  question: "How does TriServe permanently neutralize deeply embedded smoke odor without masking agents?",
  answer: "We do not use fragranced sprays or temporary masking air fresheners. Our crews deploy industrial thermal foggers that replicate the molecular size of smoke particles to penetrate wood grains and subfloors. We follow with commercial hydroxyl generators and controlled ozone treatment in sealed spaces, chemically breaking down odor-causing sulfur and nitrogen compounds at the molecular level."
}, {
  id: "item-3",
  question: "How do your teams coordinate claims directly with insurance adjusters?",
  answer: "Because we use local field crews rather than outsourced call centers, your dedicated project manager meets the adjuster on site. We compile digital moisture and thermal imaging maps, complete room-by-room itemized inventories, and submit standardized estimates using industry-standard Xactimate pricing. This eliminates guesswork, speeds up authorization, and ensures your carrier approves necessary structural mitigation immediately."
}, {
  id: "item-4",
  question: "What is the realistic timeline from initial debris cleanup to full interior rebuild?",
  answer: "Emergency board-up, water extraction from firefighting efforts, and hazardous debris removal begin within hours of dispatch. Deep soot cleaning and complete odor neutralization typically take 3 to 7 business days depending on square footage. Once structural drying and abatement clearances are certified, framing, drywall, and finish carpentry begin under our unified general contracting license, shaving weeks off multi-contractor handoffs."
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.32
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="faqs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-8 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-mono font-semibold tracking-wider text-muted-foreground uppercase">
              Field Protocol &bull; Fire, Smoke &amp; Odor Mitigation
            </span>
          </div>
          <div className="text-xs font-mono text-muted-foreground">
            Service Coverage: MD &bull; DC &bull; Northern VA
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Heading and Immediate Help Card */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="space-y-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                <ShieldAlert className="size-3.5" />
                Clear answers
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
                Answers to common fire and smoke damage questions
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
                Property fires leave corrosive soot residues and lingering airborne chemicals. Here is how our local field technicians protect salvageable structures, clear insurance hurdles, and restore safety.
              </p>
            </motion.div>

            {/* Direct Crew Support Box */}
            <motion.div variants={itemVariants} className="rounded-lg border border-border bg-muted/50 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Have an urgent question on scene?
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Direct local technicians &bull; No call center dispatch
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed">
                Fire damage worsens every hour soot sits on metals, stone, and porous finishes. Speak directly with a duty manager.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-11 px-5 rounded-md">
                  <a href="tel:855-435-6631" className="flex items-center justify-center gap-2">
                    <Phone className="size-4" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-input hover:bg-muted font-medium h-11 px-5 rounded-md">
                  <Link to="/ContactRequestServicePage" className="flex items-center justify-center gap-2">
                    <FileText className="size-4" />
                    <span>Request assessment</span>
                  </Link>
                </Button>
              </div>

              <div className="pt-2 border-t border-border/80 flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>24/7 immediate regional response across MD, DC &amp; NoVA</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Accordion Panels */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="lg:col-span-7">
            <div className="bg-card rounded-lg border border-border p-6 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
                {faqItems.map((item, index) => <motion.div key={item.id} variants={itemVariants} data-index={index} className="overflow-hidden">
                    <AccordionItem value={item.id} className="border-b border-border/70 last:border-b-0 py-1">
                      <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground hover:no-underline hover:text-primary py-4 text-left transition-colors">
                        <span className="pr-4">{item.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>)}
              </Accordion>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}